import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PublicNavHeader from '../components/PublicNavHeader';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';

import {
  ArrowRight,
  CheckCircle,
  Eye,
  FileText,
  HardDrives,
  Lock,
  Shield,
  UsersThree,
  XCircle
} from '@phosphor-icons/react';
const SecurityPage = () => {
  const { authStatus, user } = useAuth();
  const isAuthenticated = authStatus === 'authenticated' && user;
  
  return (
    <>
      <Helmet>
        <title>Security, Privacy & Governance | Man & Machine</title>
        <meta name="description" content="Learn how Man & Machine protects your data with organisation-level isolation, LLM key ownership, audit logging, and enterprise-grade security controls." />
        <meta name="keywords" content="AI security, data privacy, enterprise AI, SOC 2, GDPR, AI governance, data isolation" />
        <link rel="canonical" href="https://manandmachine.ai/security" />
        
        {/* Open Network */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://manandmachine.ai/security" />
        <meta property="og:title" content="Security, Privacy & Governance | Man & Machine" />
        <meta property="og:description" content="Learn how Man & Machine protects your data with organisation-level isolation, LLM key ownership, and audit logging." />
        <meta property="og:image" content="https://manandmachine.ai/og-security.jpg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Security, Privacy & Governance | Man & Machine" />
        <meta name="twitter:description" content="Learn how Man & Machine protects your data with organisation-level isolation, LLM key ownership, and audit logging." />
        
        {/* JSON-LD Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Help",
                "name": "How does Man & Machine protect my data?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Man & Machine uses organisation-level data isolation, encryption at rest and in transit, and strict access controls. Your data is never shared with other organisations."
                }
              },
              {
                "@type": "Help",
                "name": "Who owns the LLM API keys?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Your organisation owns and controls your LLM API keys. You can use your own keys or organisation-provided keys with full visibility into usage."
                }
              },
              {
                "@type": "Help",
                "name": "Can AI agents act autonomously without approval?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No. Man & Machine requires explicit user confirmation for all sensitive operations. Agents do not execute actions autonomously without governance controls."
                }
              }
            ]
          })}
        </script>
      </Helmet>
      
      <PublicNavHeader />
      
      <div className="min-h-screen bg-[#0a0a0f]">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#6366F1]/10 via-transparent to-[#EC4899]/10" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#6366F1]/20 blur-[120px] rounded-full" />
          
          <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center max-w-3xl mx-auto"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#6366F1]/10 border border-[#6366F1]/20 text-[#6366F1] text-sm font-medium mb-6">
                <Shield size={16} />
                Security & Governance
              </div>
              
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                Security, Privacy & Governance
              </h1>
              
              <p className="text-lg text-[#A0AEC0] mb-4">
                Man & Machine is a platform built for enterprise trust. Your data stays within your organisation, AI agents operate under strict governance controls, and you maintain full ownership of your LLM keys.
              </p>
              
              <p className="text-base text-[#718096]">
                We believe AI should augment human decision-making, not replace it. Every sensitive action requires human confirmation.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Core Security Principles */}
        <section className="max-w-7xl mx-auto px-6 pb-16">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Core Security Principles</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: UsersThree, title: 'Organisation-Level Isolation', description: 'Your data is completely isolated from other organisations. No cross-tenant data access is possible.', color: 'blue' },
              { icon: Lock, title: 'LLM Key Ownership', description: 'You own and control your LLM API keys. Use your own keys or organisation-provided keys with full visibility.', color: 'purple' },
              { icon: Eye, title: 'No Autonomous Execution', description: 'AI agents do not act without human confirmation. Sensitive operations always require explicit approval.', color: 'green' },
              { icon: FileText, title: 'Audit Logging', description: 'Every agent interaction, command execution, and configuration change is logged for compliance and review.', color: 'amber' },
              { icon: HardDrives, title: 'Encryption', description: 'All data is encrypted at rest (AES-256) and in transit (TLS 1.3). We follow industry best practices.', color: 'red' },
              { icon: Shield, title: 'Access Controls', description: 'Role-based permissions control who can create, edit, run, or delete agents within your organisation.', color: 'indigo' }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="glass rounded-2xl p-6"
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                  item.color === 'blue' ? 'bg-blue-500/10' :
                  item.color === 'purple' ? 'bg-purple-500/10' :
                  item.color === 'green' ? 'bg-green-500/10' :
                  item.color === 'amber' ? 'bg-amber-500/10' :
                  item.color === 'red' ? 'bg-red-500/10' : 'bg-indigo-500/10'
                }`}>
                  <item.icon size={24} className={`${
                    item.color === 'blue' ? 'text-blue-400' :
                    item.color === 'purple' ? 'text-purple-400' :
                    item.color === 'green' ? 'text-green-400' :
                    item.color === 'amber' ? 'text-amber-400' :
                    item.color === 'red' ? 'text-red-400' : 'text-indigo-400'
                  }`} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-[#A0AEC0] text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Does / Does Not Section */}
        <section className="max-w-7xl mx-auto px-6 pb-16">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">What Man & Machine Does and Does Not Do</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="glass rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <CheckCircle size={20} className="text-green-400" />
                What We Do
              </h3>
              <ul className="space-y-3">
                {[
                  'Isolate your data at the organisation level',
                  'Give you full ownership of LLM API keys',
                  'Require confirmation for sensitive operations',
                  'Log all agent activity for audit and compliance',
                  'Encrypt data at rest and in transit',
                  'Provide role-based access controls',
                  'Enable you to revoke access at any time'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-[#A0AEC0] text-sm">
                    <CheckCircle size={14} className="text-green-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="glass rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <XCircle size={20} className="text-red-400" />
                What We Never Do
              </h3>
              <ul className="space-y-3">
                {[
                  'Share your data with other organisations',
                  'Allow cross-tenant data access',
                  'Execute sensitive actions without confirmation',
                  'Store voice recordings after transcription',
                  'Train models on your organisation\'s data',
                  'Allow agents to act autonomously without controls',
                  'Bypass your organisation\'s security policies'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-[#A0AEC0] text-sm">
                    <XCircle size={14} className="text-red-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="max-w-7xl mx-auto px-6 pb-16">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Frequently Asked Helps</h2>
          
          <div className="space-y-4">
            {[
              {
                q: 'How does Man & Machine protect my data?',
                a: 'Man & Machine uses organisation-level data isolation, encryption at rest and in transit, and strict access controls. Your data is never shared with other organisations.'
              },
              {
                q: 'Who owns the LLM API keys?',
                a: 'Your organisation owns and controls your LLM API keys. You can use your own keys or organisation-provided keys with full visibility into usage and spending.'
              },
              {
                q: 'Can AI agents act autonomously without approval?',
                a: 'No. Man & Machine requires explicit user confirmation for all sensitive operations. Agents do not execute actions autonomously without governance controls.'
              },
              {
                q: 'Is my data used to train AI models?',
                a: 'No. Your organisation\'s data is never used to train AI models. Your data remains private and isolated within your organisation.'
              },
              {
                q: 'What compliance standards do you support?',
                a: 'Man & Machine is built with SOC 2, GDPR, and enterprise security requirements in mind. Contact us for specific compliance documentation.'
              }
            ].map((faq, i) => (
              <div key={i} className="glass rounded-xl p-6">
                <h3 className="text-white font-semibold mb-2">{faq.q}</h3>
                <p className="text-[#A0AEC0] text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="max-w-7xl mx-auto px-6 pb-20">
          <div className="glass rounded-2xl p-8 text-center">
            <Shield size={32} className="text-[#6366F1] mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">Have Security Helps?</h2>
            <p className="text-[#A0AEC0] mb-6 max-w-xl mx-auto">
              Our team is happy to discuss security, compliance, and governance requirements for your organisation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
              >
                Contact Security Team
                <ArrowRight size={18} />
              </Link>
              {!isAuthenticated && (
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 glass border border-[rgba(255,255,255,0.1)] text-white font-medium rounded-lg hover:bg-[rgba(255,255,255,0.05)] transition-colors"
                >
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </section>
      </div>
      
      <Footer />
    </>
  );
};

export default SecurityPage;
